import { LANG } from '~/enums/Lang';
import type {TranslationBatchModel} from "~/composables/useLocale";
import {usePartnerConfig} from "~/stores/usePartnerConfig";
import { config as uiConfig } from '@common/ui-kit';

export interface LanguageModel {
  id: number;
  name: string;
  original_name?: string | null;
  nativeName?: string | null;
  isoCode?: LANG;
  iso_code?: LANG;
}

export const useLocaleStore = () => {
  const { t } = useLocale();
  const { apiService } = useApi();
  const { countryCode } = usePartnerConfig();

  const isLocaleReady = useState(() => false);
  const switchLocalePath = useSwitchLocalePath();
  const localeCookie = useCookie('locale');
  const router = useRouter();
  const route = useRoute();
  const locale = useCookie('locale');
  const currentIsoCode = useState<LANG>('currentIsoCode', () => (route?.name as string)?.split('___')[1] as LANG || locale.value as LANG || LANG.EN);
  const currentLocaleId = useState<number>('currentLocaleId', () => 2);
  const languages = useState('languages', () => {
    return {
      data: [],
    }
  });

  const setUiConfigTranslates = () => {
    uiConfig.uiLanguages.translations.modalSearchNotFound = t('Ничего не найдено');
    uiConfig.uiLanguages.translations.searchInputPlaceholder = t('Поиск');
    uiConfig.uiLanguages.translations.changeLanguage = t('Сменить язык');
  };

  const mappedLanguages = computed(() => {
    return languages.value.data.map((lang: LanguageModel) => {
      return {
        ...lang,
        nativeName: lang.original_name,
        isoCode: lang.iso_code,
      };
    });
  });

  const currentLanguage = computed(() => {
    return mappedLanguages.value.find((lang: LanguageModel) => lang.isoCode === currentIsoCode.value);
  });

  const setLocale = async (lang: LanguageModel) => {
    try {
      if (currentIsoCode.value !== lang.isoCode) {
        currentIsoCode.value = lang.isoCode as LANG;
        localeCookie.value = lang.isoCode;
        await getCurrentLanguage();
      }
      if (!!switchLocalePath(lang.isoCode!)) {
        await router.push(switchLocalePath(lang.isoCode!));
      } else {
        const routeArr = route.path.slice(1, -1).split('/');
        routeArr[0] = lang.isoCode!;
        const newRoute = routeArr.join('/');
        await router.push(`/${newRoute}`);
      }
      setUiConfigTranslates();
    } catch (err: any) {
      throw new Error(err);
    }
  };

  const newTranslations = useState<TranslationBatchModel[]>('newTranslations', () => []);
  const json = useState<any>('json', () => null);
  const languagesLoaded = useState<boolean>('languagesLoaded', () => false);
  const visibleSxLangsModal = useState<boolean>('visibleSxLangsModal', () => false);
  const excludedLangCodes = shallowRef([
    'hi', 'bn', 'sw', 'vi',
  ]);

  function convertLocale(language: string) {
    return language ? language.slice(0, 2) : '';
  }

  const browserLocales = computed(() => {
    const locales = navigator?.languages.map((lang) => convertLocale(lang));
    return [...new Set(locales)];
  });

  const frequentlyUsedLocales = computed(() => {
    const locales = [
      LANG.EN,
      countryCode.value.toLowerCase(),
      ...browserLocales.value,
    ];
    return [...new Set(locales)];
  });

  const frequentlyUsedLanguages = computed(() => {
    return languages.value.data.filter((lang: LanguageModel) => frequentlyUsedLocales.value.includes(lang.iso_code!)) ?? [];
  });

  async function getCurrentLanguage() {
    try {
      isLocaleReady.value = false;
      json.value = await apiService.get(`i18n/${currentIsoCode.value}.json`);
      isLocaleReady.value = true;
    } catch (e: any) {
      console.error(e);
      ElNotification({
        message: `${e.message}`,
        type: 'error',
      });
    }
  }

  async function getAllLanguages() {
    try {
      if (!languages.value.data?.length) {
        const resp = await apiService.get('i18n/languages');
        languages.value.data = resp.data.filter((e: { iso_code: string }) => !excludedLangCodes.value.includes(e.iso_code));
        await getCurrentLanguage();
        languagesLoaded.value = true;
      }
    } catch (e: any) {
      console.error(e);
      ElNotification({
        message: `${e.message}`,
        type: 'error',
      });
    }
  }

  async function postTranslationBatchHandler() {
    if (!newTranslations.value.length) {
      return;
    }
    await apiService.post('translation/batch', { contents: newTranslations.value })
    newTranslations.value = [];
  }

  return {
    isLocaleReady,
    currentIsoCode,
    currentLocaleId,
    languages,
    locale,
    json,
    languagesLoaded,
    visibleSxLangsModal,
    newTranslations,
    frequentlyUsedLanguages,
    currentLanguage,
    mappedLanguages,
    setUiConfigTranslates,
    setLocale,
    getCurrentLanguage,
    getAllLanguages,
    postTranslationBatchHandler,
  }
}